/**
 * Utility functions for calculating and verifying file checksums
 */

// Maximum file size for checksum calculation (100MB)
// Files larger than this will skip checksum verification to prevent memory issues
const MAX_CHECKSUM_FILE_SIZE = 100 * 1024 * 1024;

/**
 * Calculate SHA-256 checksum for a file or blob
 * @param file - File or Blob to calculate checksum for
 * @returns Promise<string> - Hex string representation of SHA-256 hash
 * @throws Error if file is too large for safe checksum calculation
 */
export async function calculateSHA256Checksum(file: File | Blob): Promise<string> {
  // Fail fast for very large files to prevent memory issues
  if (file.size > MAX_CHECKSUM_FILE_SIZE) {
    throw new Error(
      `File too large for checksum calculation (${file.size} bytes > ${MAX_CHECKSUM_FILE_SIZE} bytes)`
    );
  }

  const hashBuffer = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("");
  return hashHex;
}

/**
 * Verify checksum from response headers against downloaded data
 * @param responseData - Blob data from response
 * @param expectedChecksum - Expected checksum from x-checksum header
 * @returns Promise<boolean> - True if checksums match
 */
export async function verifyDownloadChecksum(
  responseData: Blob,
  expectedChecksum: string
): Promise<boolean> {
  if (!expectedChecksum) {
    throw new Error("Checksum header not found");
  }

  try {
    const calculatedChecksum = await calculateSHA256Checksum(responseData);
    if (calculatedChecksum !== expectedChecksum) {
      throw new Error("Checksum mismatch");
    }
  } catch (error) {
    if (error.message.includes("File too large for checksum calculation")) {
      //eslint-disable-next-line no-console
      console.warn("Skipping checksum verification for large file:", error.message);
      return true; // Skip verification for large files
    }
    throw error; // Re-throw other errors
  }

  return true;
}
